import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ErrorAlert } from "../components/ErrorAlert";
import { LoserPrizeImageForm } from "../components/LoserPrizeImageForm";
import { useTRPC } from "../lib/trpc";

/**
 * The platform default for the battle loser prize's image: what every environment's battles show
 * for the prize until its tenant sets one under Storefront → Battles. Platform admins only.
 */
export function PlatformBattlesPage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const options = trpc.platform.loserPrize.get.queryOptions();
  const query = useQuery(options);
  const save = useMutation(
    trpc.platform.loserPrize.set.mutationOptions({
      onSuccess: (saved) => {
        queryClient.setQueryData(options.queryKey, saved);
        void queryClient.invalidateQueries({ queryKey: options.queryKey });
        toast.success(saved.imageUrl ? "Loser prize image saved." : "Loser prize image cleared.");
      },
    }),
  );

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Battles</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          The loser prize every battle hands its non-winning players. Its image here is what every
          environment shows until its tenant sets one of its own.
        </p>
      </div>
      {query.isLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
      {query.error && <ErrorAlert message={query.error.message} />}
      {query.data && (
        <Card>
          <CardHeader>
            <CardTitle>Loser prize image</CardTitle>
            <CardDescription>
              A battle snapshots the image when it is created, so a change here reaches new battles
              only. Blank shows the storefront's placeholder.
            </CardDescription>
          </CardHeader>
          <CardContent className="gap-4">
            <LoserPrizeImageForm
              key={query.data.imageUrl}
              imageUrl={query.data.imageUrl}
              canEdit
              allowEmpty
              saving={save.isPending}
              onSave={(imageUrl) => save.mutate({ imageUrl })}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
